import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { FileText, Download, CheckCircle, AlertCircle, DollarSign, Calendar } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface MentorYearlyEarnings {
  mentor_id: string;
  mentor_name: string;
  mentor_email: string;
  total_earnings: number;
  session_count: number;
  form_1099_issued: boolean;
  w9_status: string;
}

interface YearlyEarningDetail {
  id: string;
  mentor_id: string;
  tax_year: number;
  total_gross_earnings: number;
  total_platform_fees_deducted: number;
  total_net_payout: number;
  session_count: number;
  first_payout_date: string;
  last_payout_date: string;
  form_1099_issued: boolean;
  form_1099_issued_date: string;
  form_1099_amount: number;
  notes: string;
}

export function YearlyEarningsManager() {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [mentorsRequiring1099, setMentorsRequiring1099] = useState<MentorYearlyEarnings[]>([]);
  const [selectedMentor, setSelectedMentor] = useState<MentorYearlyEarnings | null>(null);
  const [yearlyDetail, setYearlyDetail] = useState<YearlyEarningDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [issuing1099, setIssuing1099] = useState(false);
  const [form1099Amount, setForm1099Amount] = useState('');
  const [form1099Notes, setForm1099Notes] = useState('');
  const { toast } = useToast();

  // Generate year options (current year and past 7 years for tax compliance)
  const yearOptions = Array.from({ length: 8 }, (_, i) => new Date().getFullYear() - i);

  useEffect(() => {
    fetchMentorsRequiring1099();
  }, [selectedYear]);

  const fetchMentorsRequiring1099 = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('get_mentors_requiring_1099', {
        p_tax_year: selectedYear
      });

      if (error) throw error;
      setMentorsRequiring1099(data || []);
    } catch (error: any) {
      console.error('Error fetching 1099 data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load 1099 data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchMentorYearlyDetail = async (mentorId: string) => {
    try {
      const { data, error } = await supabase
        .from('mentor_yearly_earnings')
        .select('*')
        .eq('mentor_id', mentorId)
        .eq('tax_year', selectedYear)
        .single();

      if (error) throw error;
      setYearlyDetail(data);
    } catch (error: any) {
      console.error('Error fetching yearly detail:', error);
      toast({
        title: 'Error',
        description: 'Failed to load yearly earnings detail',
        variant: 'destructive',
      });
    }
  };

  const handleMentorSelect = (mentor: MentorYearlyEarnings) => {
    setSelectedMentor(mentor);
    setForm1099Amount((mentor.total_earnings / 100).toFixed(2)); // Convert from cents
    setForm1099Notes('');
    fetchMentorYearlyDetail(mentor.mentor_id);
  };

  const handleIssue1099 = async () => {
    if (!selectedMentor || !form1099Amount) return;
    
    setIssuing1099(true);
    try {
      const amountInCents = Math.round(parseFloat(form1099Amount) * 100);
      
      const { error } = await supabase.rpc('mark_1099_issued', {
        p_mentor_id: selectedMentor.mentor_id,
        p_tax_year: selectedYear,
        p_form_amount: amountInCents,
        p_notes: form1099Notes || null
      });

      if (error) throw error;

      toast({
        title: 'Success',
        description: '1099 marked as issued successfully',
      });

      // Refresh data
      await fetchMentorsRequiring1099();
      setSelectedMentor(null);
      setYearlyDetail(null);
    } catch (error: any) {
      console.error('Error issuing 1099:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to issue 1099',
        variant: 'destructive',
      });
    } finally {
      setIssuing1099(false);
    }
  };

  const refreshYearlyEarnings = async () => {
    setLoading(true);
    try {
      // This would typically call a function to recalculate all yearly earnings
      // For now, just refresh the current view
      await fetchMentorsRequiring1099();
      toast({
        title: 'Success',
        description: 'Yearly earnings data refreshed',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'Failed to refresh data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const totalEarningsFor1099 = mentorsRequiring1099.reduce((sum, mentor) => sum + mentor.total_earnings, 0);
  const mentorsIssued = mentorsRequiring1099.filter(m => m.form_1099_issued).length;
  const mentorsPending = mentorsRequiring1099.filter(m => !m.form_1099_issued).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Yearly Earnings & 1099 Management</h2>
          <p className="text-muted-foreground">
            Track mentor earnings by tax year and manage 1099 reporting
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Select value={selectedYear.toString()} onValueChange={(value) => setSelectedYear(parseInt(value))}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map(year => (
                <SelectItem key={year} value={year.toString()}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={refreshYearlyEarnings} disabled={loading}>
            Refresh Data
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total 1099 Required</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{mentorsRequiring1099.length}</div>
            <p className="text-xs text-muted-foreground">
              Mentors earning ≥ $600 in {selectedYear}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Earnings</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${(totalEarningsFor1099 / 100).toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              Subject to 1099 reporting
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">1099s Issued</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{mentorsIssued}</div>
            <p className="text-xs text-muted-foreground">
              Completed for {selectedYear}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending 1099s</CardTitle>
            <AlertCircle className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{mentorsPending}</div>
            <p className="text-xs text-muted-foreground">
              Require action
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Mentors Table */}
      <Card>
        <CardHeader>
          <CardTitle>Mentors Requiring 1099 - Tax Year {selectedYear}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <div className="text-muted-foreground">Loading...</div>
            </div>
          ) : mentorsRequiring1099.length === 0 ? (
            <div className="text-center p-8 text-muted-foreground">
              No mentors requiring 1099s found for {selectedYear}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Mentor Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Total Earnings</TableHead>
                  <TableHead>Sessions</TableHead>
                  <TableHead>W-9 Status</TableHead>
                  <TableHead>1099 Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mentorsRequiring1099.map((mentor) => (
                  <TableRow key={mentor.mentor_id}>
                    <TableCell className="font-medium">{mentor.mentor_name}</TableCell>
                    <TableCell>{mentor.mentor_email}</TableCell>
                    <TableCell>${(mentor.total_earnings / 100).toLocaleString()}</TableCell>
                    <TableCell>{mentor.session_count}</TableCell>
                    <TableCell>
                      <Badge variant={mentor.w9_status === 'approved' ? 'default' : 'destructive'}>
                        {mentor.w9_status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={mentor.form_1099_issued ? 'default' : 'secondary'}>
                        {mentor.form_1099_issued ? 'Issued' : 'Pending'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleMentorSelect(mentor)}
                          >
                            {mentor.form_1099_issued ? 'View' : 'Issue 1099'}
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                          <DialogHeader>
                            <DialogTitle>
                              1099 Management - {mentor.mentor_name}
                            </DialogTitle>
                          </DialogHeader>
                          
                          {yearlyDetail && (
                            <div className="space-y-6">
                              {/* Earnings Summary */}
                              <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <Label>Gross Session Value</Label>
                                  <div className="text-lg font-semibold">
                                    ${(yearlyDetail.total_gross_earnings / 100).toLocaleString()}
                                  </div>
                                </div>
                                <div className="space-y-2">
                                  <Label>Platform Fees Deducted</Label>
                                  <div className="text-lg font-semibold text-red-600">
                                    -${(yearlyDetail.total_platform_fees_deducted / 100).toLocaleString()}
                                  </div>
                                </div>
                                <div className="space-y-2">
                                  <Label>Net Payout (1099 Amount)</Label>
                                  <div className="text-xl font-bold text-green-600">
                                    ${(yearlyDetail.total_net_payout / 100).toLocaleString()}
                                  </div>
                                </div>
                                <div className="space-y-2">
                                  <Label>Total Sessions</Label>
                                  <div className="text-lg font-semibold">
                                    {yearlyDetail.session_count}
                                  </div>
                                </div>
                              </div>

                              {/* Date Range */}
                              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                <div className="flex items-center gap-2">
                                  <Calendar className="h-4 w-4" />
                                  <span>
                                    {yearlyDetail.first_payout_date} to {yearlyDetail.last_payout_date}
                                  </span>
                                </div>
                              </div>

                              {/* 1099 Form Management */}
                              {!mentor.form_1099_issued ? (
                                <div className="border-t pt-4 space-y-4">
                                  <h4 className="font-semibold">Issue 1099 Form</h4>
                                  <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                      <Label htmlFor="form1099Amount">1099 Amount</Label>
                                      <Input
                                        id="form1099Amount"
                                        type="number"
                                        step="0.01"
                                        value={form1099Amount}
                                        onChange={(e) => setForm1099Amount(e.target.value)}
                                        placeholder="0.00"
                                      />
                                    </div>
                                  </div>
                                  <div className="space-y-2">
                                    <Label htmlFor="form1099Notes">Notes (Optional)</Label>
                                    <Textarea
                                      id="form1099Notes"
                                      value={form1099Notes}
                                      onChange={(e) => setForm1099Notes(e.target.value)}
                                      placeholder="Add any notes about this 1099..."
                                      rows={3}
                                    />
                                  </div>
                                  <Button 
                                    onClick={handleIssue1099}
                                    disabled={!form1099Amount || issuing1099}
                                    className="w-full"
                                  >
                                    {issuing1099 ? 'Issuing...' : 'Mark 1099 as Issued'}
                                  </Button>
                                </div>
                              ) : (
                                <div className="border-t pt-4 space-y-2">
                                  <h4 className="font-semibold text-green-600">1099 Issued</h4>
                                  <div className="text-sm text-muted-foreground">
                                    <p>Amount: ${(yearlyDetail.form_1099_amount / 100).toFixed(2)}</p>
                                    <p>Date Issued: {yearlyDetail.form_1099_issued_date}</p>
                                    {yearlyDetail.notes && <p>Notes: {yearlyDetail.notes}</p>}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </DialogContent>
                      </Dialog>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}